import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, Optional, Inject, DoCheck, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LabUserRequestComplete } from 'node_js/models/business/lab-user-request-complete';
import { LabUserComplete } from 'node_js/models/business/lab/lab-user-complete';
import { LabComplete } from 'node_js/models/business/lab/lab-complete';
import { UserContact } from 'node_js/models/tables/user_contact';
import { RoleEnum } from 'node_js/models/enums/role';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { LabService } from 'src/app/services/lab.service';
import { GadgetService } from 'src/app/services/gadget.service';
import { generateId, presentLoading, presentSnackBar } from 'src/global';
import { LabUserCombo } from 'node_js/models/business/lab/lab-user-combo';
import { LabUserRequest } from 'node_js/models/tables/lab_user_request';
import { UserSafe } from 'node_js/models/business/user-safe';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { LabUser } from 'node_js/models/tables/lab_user';
import { Platform } from '@ionic/angular';
import { AndroidBackPriority } from 'node_js/models/enums/android-back-priority';
import { RoleHelpDialogComponent } from '../dialogs/role-help-dialog/role-help-dialog.component';
import { TutorialService } from 'src/app/services/tutorial.service';
import { LanguageService } from 'src/app/services/language.service';
import { LiveUpdatesService } from 'src/app/services/live-updates.service';

@Component({
  selector: 'lab-user-list',
  templateUrl: './lab-user-list.component.html',
  styleUrls: ['./lab-user-list.component.scss']
})
export class LabUserListComponent implements OnInit, OnChanges, OnDestroy, DoCheck, AfterViewInit {

  @Input() labComplete: LabComplete;

  RoleEnum = RoleEnum;

  availableContactsToAddToLab: UserContact[] = [];
  newLabUserContact: UserContact = null;
  newLabUserRole: RoleEnum = RoleEnum.USER;

  clonedLabUsersComplete: LabUserComplete[] = [];
  clonedLabUserRequestsComplete: LabUserRequestComplete[] = [];

  isThereAUserBeingDeleted: boolean = false;
  isThereAUserRequestBeingDeleted: boolean = false;
  isThereAnActiveUserRequest: boolean = false;

  // Back button subscription
  private backButtonSub: any;
  private tutorialOverlaySyncId: any = null;

  @ViewChild('containerRoot') containerRootRef?: ElementRef<HTMLElement>;
  @ViewChild('roleGuideButton') roleGuideButtonRef?: ElementRef<HTMLElement>;

  localTutorialShadeStyles: Record<string, string>[] = [];

  constructor(
    private userAuthService: UserAuthService,
    private labService: LabService,
    private gadgetService: GadgetService,
    private matSnackBar: MatSnackBar,
    private matDialog: MatDialog,
    private platform: Platform,
    public tutorialService: TutorialService,
    private languageService: LanguageService,
    private liveUpdatesService: LiveUpdatesService,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: { labComplete: LabComplete },
    @Optional() private dialogRef?: MatDialogRef<LabUserListComponent>
  ) {
    // If dialog data is provided, use it; otherwise rely on @Input
    if (this.dialogData && this.dialogData.labComplete) {
      this.labComplete = this.dialogData.labComplete;
    }
  }

  ngDoCheck() {
    if (this.dialogRef) {
      this.dialogRef.disableClose = this.tutorialService.isLabMembersTutorialActive;
    }

    if (typeof document !== 'undefined') {
      document.body.classList.toggle('tutorial-lab-members-active', this.tutorialService.isLabMembersTutorialActive);
    }
  }

  ngAfterViewInit() {
    this.syncLocalTutorialOverlay();
    this.tutorialOverlaySyncId = setInterval(() => {
      this.syncLocalTutorialOverlay();
    }, 150);
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('[LabUserList] ngOnChanges called with changes:', changes);

    if (changes['labComplete'] && changes['labComplete'].currentValue) {
      console.log('[LabUserList] ngOnChanges: labComplete changed, preserving local changes');
      console.log('[LabUserList] Previous labComplete:', changes['labComplete'].previousValue?.lab?.id);
      console.log('[LabUserList] Current labComplete:', changes['labComplete'].currentValue?.lab?.id);

      // Store the current local state before resetting
      const currentLocalUsers = [...this.clonedLabUsersComplete];
      const currentLocalRequests = [...this.clonedLabUserRequestsComplete];

      console.log('[LabUserList] Stored local state - users:', currentLocalUsers.length, 'requests:', currentLocalRequests.length);

      // Reset the component with new data
      this.resetMembers(true);

      // Merge any local changes that haven't been synced yet
      if (currentLocalUsers.length > 0 || currentLocalRequests.length > 0) {
        console.log('[LabUserList] Merging local changes with new labComplete data');

        // Add any local users that aren't in the new data
        for (const localUser of currentLocalUsers) {
          // Check by ID first, but also check by userId+labId since the ID might change
          // (e.g., from a temporary ID to a database ID after save)
          const existsInNew = this.clonedLabUsersComplete.some(
            newUser => newUser.labUser.id === localUser.labUser.id ||
              (newUser.labUser.userId === localUser.labUser.userId &&
                newUser.labUser.labId === localUser.labUser.labId)
          );
          if (!existsInNew) {
            console.log('[LabUserList] Preserving local lab user:', localUser.userSafe.nickname);
            this.clonedLabUsersComplete.push(localUser);
          } else {
            console.log('[LabUserList] Local lab user already exists in new data (matched by id or userId+labId):', localUser.userSafe.nickname);
          }
        }

        // Add any local requests that aren't in the new data
        for (const localRequest of currentLocalRequests) {
          const existsInNew = this.clonedLabUserRequestsComplete.some(
            newRequest => newRequest.labUserRequest.id === localRequest.labUserRequest.id
          );
          if (!existsInNew) {
            console.log('[LabUserList] Preserving local lab user request for:', localRequest.userSafe.nickname);
            this.clonedLabUserRequestsComplete.push(localRequest);
          }
        }

        // Update UI state after merging
        this.setIsThereAUserBeingDeleted();
        this.setIsThereAUserRequestInfo();
        this.getAvailableContactToAddToLab();
      }
    } else {
      console.log('[LabUserList] ngOnChanges: No labComplete change or no currentValue');
    }
  }

  ngOnInit() {
    console.log('[LabUserList] ngOnInit called, labComplete:', this.labComplete?.lab?.id);

    // Only proceed if labComplete is available
    if (!this.labComplete) {
      console.log('[LabUserList] No labComplete available, returning early');
      return;
    }

    // Initialize members immediately - we need data for the template
    // The optimization is in the cloning method itself (using structuredClone when available)
    this.resetMembers(true);

    // Defer back button handler to avoid blocking UI initialization
    setTimeout(() => {
      this.handleBackButton();
    }, 0);

    this.labService.currentSelectedLabUserCreated.subscribe((labUserComplete: LabUserComplete) => {
      console.log('[LabUserList] Received currentSelectedLabUserCreated event:', labUserComplete);
      console.log('[LabUserList] Current labComplete labId:', this.labComplete?.lab?.id);
      console.log('[LabUserList] Event labId:', labUserComplete.labUser.labId);
      console.log('[LabUserList] Event labUser ID:', labUserComplete.labUser.id, 'userId:', labUserComplete.labUser.userId);

      // Check if this event is for the current lab
      if (this.labComplete && this.labComplete.lab.id === labUserComplete.labUser.labId) {
        console.log('[LabUserList] Adding new lab user to current lab:', labUserComplete.userSafe.nickname);

        // Check if this user already exists in the source data (labComplete.labUsersComplete)
        // The source data might have the correct ID even if the live update has a wrong/temporary ID
        const existingInSource = this.labComplete.labUsersComplete.find(
          sourceUser => sourceUser.labUser.userId === labUserComplete.labUser.userId &&
            sourceUser.labUser.labId === labUserComplete.labUser.labId
        );

        // Use the source data if it exists (it has the correct ID), otherwise use the live update data
        const labUserToUse = existingInSource || labUserComplete;

        if (existingInSource && existingInSource.labUser.id !== labUserComplete.labUser.id) {
          console.log('[LabUserList] Found existing user in source with different ID. Source ID:', existingInSource.labUser.id, 'Live update ID:', labUserComplete.labUser.id);
          console.log('[LabUserList] Using source data to ensure correct ID');
        }

        // Check if this user already exists in cloned array (by ID or by userId+labId)
        const alreadyExistsInClone = this.clonedLabUsersComplete.some(
          existingUser => existingUser.labUser.id === labUserToUse.labUser.id ||
            (existingUser.labUser.userId === labUserToUse.labUser.userId &&
              existingUser.labUser.labId === labUserToUse.labUser.labId)
        );

        if (alreadyExistsInClone) {
          console.log('[LabUserList] Lab user already exists in clone, skipping duplicate:', labUserToUse.userSafe.nickname);
        } else {
          // Create a deep copy of the lab user complete (using the correct one from source if available)
          let clonedLabUserComplete: LabUserComplete = JSON.parse(JSON.stringify(labUserToUse));
          clonedLabUserComplete.labUser.newRoleId = clonedLabUserComplete.labUser.roleId;

          // Add the new lab user to the cloned array
          this.clonedLabUsersComplete.push(clonedLabUserComplete);
          console.log('[LabUserList] New lab user added with ID:', clonedLabUserComplete.labUser.id, 'total count:', this.clonedLabUsersComplete.length);
        }

        // Remove any lab user requests for this user since they're now a lab member
        this.removeLabUserRequestsForUserId(labUserComplete.labUser.userId);

        // Update UI state
        this.setIsThereAUserBeingDeleted();
        this.getAvailableContactToAddToLab();
      } else {
        console.log('[LabUserList] Lab ID mismatch, not processing LabUserCreated event');
      }
    })

    this.labService.currentSelectedLabUserSettingsChanged.subscribe(() => {
      console.log('[LabUserList] Received currentSelectedLabUserSettingsChanged event, resetting members');
      this.resetMembers(true);
    })

    this.liveUpdatesService.afterUserContactUpdatedObservable.subscribe(() => {
      this.getAvailableContactToAddToLab();
    })

    // Subscribe to lab user request deletion events
    this.labService.currentSelectedLabUserRequestDeleted.subscribe((data: { labUserRequestId: string, labId: string, userId?: string, removedCount?: number }) => {
      console.log('[LabUserList] Received currentSelectedLabUserRequestDeleted event:', data);
      console.log('[LabUserList] Current labComplete labId:', this.labComplete?.lab?.id);
      console.log('[LabUserList] Event labId matches current lab:', this.labComplete && this.labComplete.lab.id === data.labId);

      if (this.labComplete && this.labComplete.lab.id === data.labId) {
        if (data.labUserRequestId === 'multiple' && data.userId) {
          // Multiple requests were removed for a specific user (e.g., when user was accepted)
          console.log('[LabUserList] Removing multiple lab user requests for userId:', data.userId);
          this.removeLabUserRequestsForUserId(data.userId);
        } else {
          // Single request was deleted
          console.log('[LabUserList] Calling removeLabUserRequestLocally for lab:', data.labId);
          this.removeLabUserRequestLocally(data.labUserRequestId);
        }
      } else {
        console.log('[LabUserList] Lab ID mismatch, not processing event');
      }
    })
  }


  resetMembers(resetClone: boolean) {
    console.log('[LabUserList] resetMembers called with resetClone:', resetClone);
    console.log('[LabUserList] Current labComplete:', this.labComplete?.lab?.id);

    if (!this.labComplete) {
      console.log('[LabUserList] No labComplete available, returning early');
      return;
    }

    this.newLabUserContact = null;
    this.newLabUserRole = RoleEnum.USER;
    if (resetClone) {
      console.log('[LabUserList] Resetting clones from labComplete data');
      console.log('[LabUserList] labUsersComplete count:', this.labComplete.labUsersComplete?.length || 0);
      console.log('[LabUserList] labUserRequestsComplete count:', this.labComplete.labUserRequestsComplete?.length || 0);

      // Use structuredClone if available (faster), fallback to JSON for compatibility
      const cloneMethod: <T>(obj: T) => T = typeof (globalThis as any).structuredClone !== 'undefined'
        ? (globalThis as any).structuredClone
        : (obj: any) => JSON.parse(JSON.stringify(obj));

      this.clonedLabUsersComplete = cloneMethod(this.labComplete.labUsersComplete);
      for (let i = 0; i < this.clonedLabUsersComplete.length; i++) {
        let clonedLabUserComplete = this.clonedLabUsersComplete[i];
        clonedLabUserComplete.labUser.newRoleId = clonedLabUserComplete.labUser.roleId;
        clonedLabUserComplete.labUser.active = true;
      }

      this.clonedLabUserRequestsComplete = cloneMethod(this.labComplete.labUserRequestsComplete);
      for (let i = 0; i < this.clonedLabUserRequestsComplete.length; i++) {
        this.clonedLabUserRequestsComplete[i].labUserRequest.deleting = false;
      }

      console.log('[LabUserList] Clones reset - users:', this.clonedLabUsersComplete.length, 'requests:', this.clonedLabUserRequestsComplete.length);
    }
    this.getAvailableContactToAddToLab();
    this.setIsThereAUserBeingDeleted();
    this.setIsThereAUserRequestInfo();

    console.log('[LabUserList] resetMembers completed');
  }


  canUserEditLabUser(labUserComplete: LabUserComplete) {
    let currentLabUser = this.labComplete.currentLabUserComplete.labUser;

    // Only Admins and Owners can edit other users
    if (currentLabUser.roleId > RoleEnum.ADMIN) {
      return false;
    }

    if (currentLabUser.roleId < labUserComplete.labUser.roleId && currentLabUser.userId != labUserComplete.labUser.userId) {
      return true;
    }
    return false;
  }

  getAvailableContactToAddToLab() {
    // Optimize using Sets for O(1) lookups instead of nested loops
    const existingUserIds = new Set<string>();

    // Build set of existing lab user IDs
    for (let i = 0; i < this.clonedLabUsersComplete.length; i++) {
      existingUserIds.add(this.clonedLabUsersComplete[i].labUser.userId);
    }

    // Build set of existing lab user request IDs
    for (let i = 0; i < this.clonedLabUserRequestsComplete.length; i++) {
      existingUserIds.add(this.clonedLabUserRequestsComplete[i].labUserRequest.userId);
    }

    // Filter contacts using Set lookup (O(1) instead of O(n))
    this.availableContactsToAddToLab = this.userAuthService.currentUserComplete.userContacts.filter(
      userContact => !existingUserIds.has(userContact.contactUserId)
    );
  }

  createLabUserRequest() {

    if (this.newLabUserContact == null) {
      presentSnackBar("Select a contact to add", SnackBarColor.DANGER, this.matSnackBar);
      return;
    }
    else if (this.newLabUserRole == null) {
      presentSnackBar("Select a role", SnackBarColor.DANGER, this.matSnackBar);
      return;
    }

    let userContact = this.newLabUserContact;

    let labUserRequest: LabUserRequest = {
      id: generateId(),
      labId: this.labComplete.lab.id,
      userId: userContact.contactUserId,
      roleId: this.newLabUserRole,
      deleting: false
    }


    let userSafe: UserSafe = {
      id: null,
      firstName: "",
      lastName: "",
      profileIconId: null,
      displayName: userContact.nickname,
      lastLanguageId: null,
      nickname: userContact.nickname
    }

    let labUserRequestComplete: LabUserRequestComplete = {
      labUserRequest,
      userSafe
    }


    this.clonedLabUserRequestsComplete.push(labUserRequestComplete);
    this.resetMembers(false);
  }

  toggleDeleteLabUserRequest(labUserRequestComplete: LabUserRequestComplete, index: number) {
    labUserRequestComplete.labUserRequest.deleting = !labUserRequestComplete.labUserRequest.deleting;
    if (isNaN(Number(labUserRequestComplete.labUserRequest.id)) && labUserRequestComplete.labUserRequest.deleting == true) {
      this.clonedLabUserRequestsComplete.splice(index, 1);
    }
    this.setIsThereAUserRequestInfo();
    this.getAvailableContactToAddToLab();

  }

  toggleDeleteLabUser(labUserComplete: LabUserComplete, i: number) {
    if (this.canUserEditLabUser(labUserComplete)) {
      labUserComplete.labUser.active = !labUserComplete.labUser.active;
      this.setIsThereAUserBeingDeleted();
    }
  }

  updateLabUserSettings() {
    // Deduplicate lab users before sending to server
    // Use userId + labId as the unique identifier since the id might change
    const deduplicatedLabUsers: LabUserComplete[] = [];
    const seenUserKeys = new Set<string>();

    for (const labUserComplete of this.clonedLabUsersComplete) {
      // Create a unique key from userId + labId
      const userKey = `${labUserComplete.labUser.userId}_${labUserComplete.labUser.labId}`;

      if (!seenUserKeys.has(userKey)) {
        seenUserKeys.add(userKey);
        deduplicatedLabUsers.push(labUserComplete);
      } else {
        console.log('[LabUserList] Removing duplicate lab user before sending to server:', labUserComplete.userSafe.nickname, 'userId:', labUserComplete.labUser.userId);
      }
    }

    console.log('[LabUserList] Deduplicated lab users:', {
      original: this.clonedLabUsersComplete.length,
      deduplicated: deduplicatedLabUsers.length,
      removed: this.clonedLabUsersComplete.length - deduplicatedLabUsers.length
    });

    let loadingRef = presentLoading(this.matDialog);
    this.labService.updateLabUserSettings(this.userAuthService, this.labComplete.lab.id, deduplicatedLabUsers, this.clonedLabUserRequestsComplete).then((labUserCombo: LabUserCombo) => {
      this.labService.upsertLabUsersCompleteLocally(labUserCombo.labUsersComplete, this.userAuthService, this.gadgetService);
      this.labComplete.labUserRequestsComplete = labUserCombo.labUserRequestsComplete;
      presentSnackBar("Lab members have been updated", SnackBarColor.SUCCESS, this.matSnackBar);
    }).catch(() => {
      presentSnackBar(this.languageService.instant('generic.unexpected-error'), SnackBarColor.DANGER, this.matSnackBar);
    }).finally(() => {

      loadingRef.close();
    })
  }

  setIsThereAUserBeingDeleted() {
    let isThereAUserBeingDeleted = false;
    for (let i = 0; i < this.clonedLabUsersComplete.length; i++) {
      let labUserComplete = this.clonedLabUsersComplete[i];
      if (labUserComplete.labUser.active == false) {
        isThereAUserBeingDeleted = true;
        break;
      }
    }
    this.isThereAUserBeingDeleted = isThereAUserBeingDeleted;
  }

  setIsThereAUserRequestInfo() {
    let isThereAUserRequestBeingDeleted = false;
    let isThereAnActiveUserRequest = false;
    for (let i = 0; i < this.clonedLabUserRequestsComplete.length; i++) {
      let clonedLabUserRequestComplete = this.clonedLabUserRequestsComplete[i];
      if (clonedLabUserRequestComplete.labUserRequest.deleting == true) {
        isThereAUserRequestBeingDeleted = true;
      }
      else {
        isThereAnActiveUserRequest = true;
      }
    }
    this.isThereAUserRequestBeingDeleted = isThereAUserRequestBeingDeleted;
    this.isThereAnActiveUserRequest = isThereAnActiveUserRequest;
  }

  /**
   * Handle back button press when component is loaded as a dialog
   */
  handleBackButton() {
    console.log('[LabUserList] handleBackButton called, dialogRef exists:', !!this.dialogRef);

    // Only set up back button handler if this component is loaded as a dialog
    if (this.dialogRef) {
      console.log('[LabUserList] Setting up back button handler with priority:', AndroidBackPriority.DIALOG);
      this.backButtonSub = this.platform.backButton.subscribeWithPriority(AndroidBackPriority.DIALOG, () => {
        console.log('[LabUserList] 🎯 BACK BUTTON PRESSED! Closing dialog...');
        this.dialogRef.close();
      });
      console.log('[LabUserList] ✅ Back button handler set up successfully');
    } else {
      console.log('[LabUserList] Not loaded as dialog, skipping back button handler');
    }
  }

  /**
   * Clean up back button subscription when component is destroyed
   */
  ngOnDestroy() {
    console.log('[LabUserList] ngOnDestroy called, cleaning up back button subscription');
    this.cleanupBackButtonSubscription();

    if (typeof document !== 'undefined') {
      document.body.classList.remove('tutorial-lab-members-active');
    }

    if (this.tutorialOverlaySyncId) {
      clearInterval(this.tutorialOverlaySyncId);
      this.tutorialOverlaySyncId = null;
    }
  }

  /**
   * Clean up back button subscription manually
   */
  private cleanupBackButtonSubscription() {
    if (this.backButtonSub) {
      try {
        this.backButtonSub.unsubscribe();
        console.log('[LabUserList] ✅ Back button subscription cleaned up');
      } catch (err) {
        console.warn('[LabUserList] Error cleaning up back button subscription:', err);
      }
      this.backButtonSub = null;
    }
  }

  /**
   * Public method to cleanup back button subscription before dialog closes
   */
  public cleanupBeforeClose() {
    console.log('[LabUserList] cleanupBeforeClose called');
    this.cleanupBackButtonSubscription();
  }

  private removeLabUserRequestLocally(labUserRequestId: string) {
    console.log('[LabUserList] removeLabUserRequestLocally called with:', labUserRequestId);
    console.log('[LabUserList] Initial clonedLabUserRequestsComplete count:', this.clonedLabUserRequestsComplete.length);

    const initialLength = this.clonedLabUserRequestsComplete.length;
    this.clonedLabUserRequestsComplete = this.clonedLabUserRequestsComplete.filter(
      (labUserRequestComplete) => {
        const matches = labUserRequestComplete.labUserRequest.id !== labUserRequestId;
        console.log('[LabUserList] Filtering request:', {
          requestId: labUserRequestComplete.labUserRequest.id,
          targetId: labUserRequestId,
          matches
        });
        return matches;
      }
    );

    const finalLength = this.clonedLabUserRequestsComplete.length;
    console.log('[LabUserList] Filtered result:', { initialLength, finalLength, removed: initialLength - finalLength });

    if (this.clonedLabUserRequestsComplete.length < initialLength) {
      console.log('[LabUserList] Request was removed, updating UI state');
      this.setIsThereAUserRequestInfo();
      this.getAvailableContactToAddToLab();
    } else {
      console.log('[LabUserList] No request was removed');
    }

    console.log('[LabUserList] removeLabUserRequestLocally completed');
  }

  private removeLabUserRequestsForUserId(userId: string) {
    console.log('[LabUserList] removeLabUserRequestsForUserId called with userId:', userId);
    console.log('[LabUserList] Initial clonedLabUserRequestsComplete count:', this.clonedLabUserRequestsComplete.length);

    const initialLength = this.clonedLabUserRequestsComplete.length;
    this.clonedLabUserRequestsComplete = this.clonedLabUserRequestsComplete.filter(
      (labUserRequestComplete) => {
        const matches = labUserRequestComplete.labUserRequest.userId !== userId;
        console.log('[LabUserList] Filtering request for userId:', {
          requestUserId: labUserRequestComplete.labUserRequest.userId,
          targetUserId: userId,
          matches
        });
        return matches;
      }
    );

    const finalLength = this.clonedLabUserRequestsComplete.length;
    console.log('[LabUserList] Filtered result for userId:', { initialLength, finalLength, removed: initialLength - finalLength });

    if (this.clonedLabUserRequestsComplete.length < initialLength) {
      console.log('[LabUserList] Requests for userId were removed, updating UI state');
      this.setIsThereAUserRequestInfo();
      this.getAvailableContactToAddToLab();
    } else {
      console.log('[LabUserList] No requests for userId were removed');
    }

    console.log('[LabUserList] removeLabUserRequestsForUserId completed');
  }

  private syncLocalTutorialOverlay() {
    if (!this.tutorialService.isStepActive('lab-members-help') || !this.containerRootRef?.nativeElement || !this.roleGuideButtonRef?.nativeElement) {
      this.localTutorialShadeStyles = [];
      return;
    }

    const containerRect = this.containerRootRef.nativeElement.getBoundingClientRect();
    const buttonRect = this.roleGuideButtonRef.nativeElement.getBoundingClientRect();

    if (!containerRect.width || !containerRect.height || !buttonRect.width || !buttonRect.height) {
      this.localTutorialShadeStyles = [];
      return;
    }

    const padding = 10;
    const top = Math.max(0, buttonRect.top - containerRect.top - padding);
    const left = Math.max(0, buttonRect.left - containerRect.left - padding);
    const right = Math.min(containerRect.width, buttonRect.right - containerRect.left + padding);
    const bottom = Math.min(containerRect.height, buttonRect.bottom - containerRect.top + padding);
    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);

    this.localTutorialShadeStyles = [
      { top: '0px', left: '0px', width: '100%', height: `${top}px` },
      { top: `${top}px`, left: '0px', width: `${left}px`, height: `${height}px` },
      { top: `${top}px`, left: `${right}px`, width: `${Math.max(0, containerRect.width - right)}px`, height: `${height}px` },
      { top: `${bottom}px`, left: '0px', width: '100%', height: `${Math.max(0, containerRect.height - bottom)}px` },
    ];
  }

  startLabMembersTutorial() {
    this.tutorialService.startLabMembersTutorial();
  }

  getLabRoleTooltip(roleId: RoleEnum): string {
    let tooltip = 'Can view this Lab.';

    switch (roleId) {
      case RoleEnum.OWNER:
        tooltip = 'Owns this Lab and has full control over settings, members, gadgets, and automation.';
        break;
      case RoleEnum.ADMIN:
        tooltip = 'Can manage this Lab, its members, gadgets, and automation.';
        break;
      case RoleEnum.CREATOR:
        tooltip = 'Can build and edit gadgets, components, and controllers in this Lab.';
        break;
      case RoleEnum.SUPPORT:
        tooltip = 'Can help monitor and troubleshoot this Lab without full admin access.';
        break;
      case RoleEnum.USER:
      default:
        tooltip = 'Can view and use shared Lab features.';
        break;
    }

    return this.languageService.translateUiLiteral(tooltip);
  }

  openRoleHelp() {
    const dialogRef = this.matDialog.open(RoleHelpDialogComponent, {
      width: '400px',
      autoFocus: false,
      panelClass: 'role-help-dialog-panel'
    });

    dialogRef.afterOpened().subscribe(() => {
      this.tutorialService.noteRoleHelpOpened();
    });

    dialogRef.afterClosed().subscribe(() => {
      this.tutorialService.noteRoleHelpClosed();

      if (this.tutorialService.justCompletedTutorialId === 'lab-members') {
        this.dialogRef?.close();
      }
    });
  }
}
